'use client'

import { Check, Package, Store } from 'lucide-react'
import type { FulfillmentOptions } from '@sifly/shared-utils'
import type { PublicPickupLocation } from '@/lib/cartFulfillment'

/**
 * Pickup / Shipping cards in the cart drawer. Presentation only: which cards
 * are available comes from /api/checkout/fulfillment-options, and the checkout
 * routes re-check the choice. Same selected treatment as the drawer's payment
 * cards (one "selected" identity across the storefront).
 */
export default function FulfillmentChoice({
  options,
  locations,
  method,
  locationId,
  shippingLabel,
  onMethodChange,
  onLocationChange,
}: {
  options: FulfillmentOptions
  locations: PublicPickupLocation[]
  method: 'ship' | 'pickup'
  locationId: string | null
  /** e.g. "$300" or "Free" — the drawer already knows the ship price. */
  shippingLabel: string
  onMethodChange: (method: 'ship' | 'pickup') => void
  onLocationChange: (locationId: string) => void
}) {
  const selectedLocation = locations.find((l) => l.id === locationId) ?? locations[0] ?? null

  const card = (
    key: 'pickup' | 'ship',
    title: string,
    Icon: typeof Store,
    detail: string,
  ) => {
    const option = options[key]
    const selected = method === key && option.available
    return (
      <button
        type="button"
        onClick={() => option.available && onMethodChange(key)}
        disabled={!option.available}
        aria-pressed={selected}
        className={`flex-1 min-w-0 rounded-xl border px-3 py-2.5 text-left transition-colors disabled:cursor-not-allowed ${
          selected
            ? 'border-[var(--color-selected)] bg-[var(--color-selected-soft)]'
            : option.available
              ? 'border-[var(--color-border)] hover:bg-[var(--color-surface-muted)]'
              : 'border-[var(--color-border)] opacity-60'
        }`}
      >
        <span className="flex items-start justify-between gap-2">
          <span className="block text-sm font-medium text-[var(--color-text)]">{title}</span>
          {selected
            ? <Check size={16} className="text-[var(--color-selected-readable)] flex-shrink-0" aria-hidden="true" />
            : <Icon size={16} className="text-[var(--color-text-muted)] flex-shrink-0" aria-hidden="true" />}
        </span>
        <span className="block text-xs text-[var(--color-text-secondary)] mt-0.5 truncate">
          {option.available ? detail : option.reason}
        </span>
      </button>
    )
  }

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-[var(--color-text-secondary)]">How do you want it?</p>
      <div className="flex gap-2">
        {card('pickup', 'Pickup', Store, `Free · ${selectedLocation?.name ?? 'In store'}`)}
        {card('ship', 'Shipping', Package, shippingLabel)}
      </div>

      {method === 'pickup' && options.pickup.available && selectedLocation && (
        locations.length > 1 ? (
          <fieldset className="space-y-1.5">
            <legend className="sr-only">Pickup location</legend>
            {locations.map((l) => (
              <label key={l.id} className="flex items-start gap-2 cursor-pointer text-xs text-[var(--color-text-secondary)]">
                <input
                  type="radio"
                  name="pickup-location"
                  value={l.id}
                  checked={selectedLocation.id === l.id}
                  onChange={() => onLocationChange(l.id)}
                  className="mt-0.5 accent-[var(--color-selected)]"
                />
                <span className="min-w-0">
                  <span className="block font-medium text-[var(--color-text)]">{l.name}</span>
                  {l.addressLine && <span className="block">{l.addressLine}</span>}
                  {l.todayHours && <span className="block">{l.todayHours}</span>}
                </span>
              </label>
            ))}
          </fieldset>
        ) : (
          <p className="text-xs text-[var(--color-text-secondary)]">
            {[selectedLocation.addressLine, selectedLocation.todayHours].filter(Boolean).join(' · ')}
          </p>
        )
      )}
    </div>
  )
}
